import { X, Image } from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import { fileAPI } from '@Lib/axios/file';
import { useAuth } from '@Context/CognitoAuthProvider';
import { BounceLoader } from 'react-spinners';
import { useGeneratorContext } from '@Context/GeneratorContext';
import { FeatDescrip } from '@Models/types';
import { getMimeType } from '@Utils/FileAttachmentUtils';

interface Attachment {
  key: string;
  fileName: string;
}

const ImageAnalyzer = () => {
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [prompt, setPrompt] = useState('');
  const [processing, setProcessing] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { logout } = useAuth();
  const fileApi = fileAPI(logout);
  const { selectedRequirements, setSelectedRequirements } =
    useGeneratorContext();

  useEffect(() => {
    if (prompt === '' && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [prompt]);

  const handleRemoveAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileUpload = async (file: File) => {
    try {
      setProcessing(true);
      const mimeType = await getMimeType(file);
      const { uploadUrl, key } = await fileApi.getUploadUrl(
        mimeType,
        file.name
      );

      await fileApi.uploadFile(uploadUrl, file, mimeType);

      setAttachment({
        key,
        fileName: file.name,
      });
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleAnalyze = async () => {
    const messageText = attachment
      ? `${prompt}[image:${attachment.key}]`
      : prompt;

    setPrompt('');
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    const imageFeatureInput: FeatDescrip = {
      id: `img-${crypto.randomUUID()}`,
      reference_num: `${attachment?.key.split('-')[0].toUpperCase()}`,
      name: `IMAGE: ${attachment?.fileName}`,
      description_body: messageText,
    };
    const updatedSelectedItems = [...selectedRequirements, imageFeatureInput];

    setSelectedRequirements(updatedSelectedItems);
  };

  return (
    <div>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="relative flex items-end space-x-2"
      >
        <div className="relative flex flex-grow flex-col">
          {attachment && (
            <div className="mb-2 flex w-1/4 items-center gap-2 rounded-lg bg-blue-100 px-3 py-1 shadow-md">
              <Image size={20} className="text-primary-700" />
              <span className="max-w-[200px] truncate text-xs text-primary-700">
                {attachment.fileName}
              </span>
              <button
                type="button"
                onClick={handleRemoveAttachment}
                className="ml-auto rounded-full p-1 hover:bg-blue-200"
              >
                <X size={16} className="text-primary-700" />
              </button>
            </div>
          )}
          <div className="relative flex items-center">
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
              }}
              placeholder="Description or Additional information"
              className="custom-scroll mx-1 h-auto w-full resize-none overflow-y-auto rounded-lg border border-white px-2 pt-1 text-primary-700 placeholder:italic focus:outline-primary-400 md:mx-2"
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(file);
                }
              }}
              accept="image/*"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={processing}
              className="absolute right-5 top-3"
            >
              <Image
                size={25}
                weight="bold"
                className={` ${
                  processing
                    ? 'text-gray-300'
                    : 'text-primary-700 hover:text-primary-500'
                }`}
              />
            </button>
          </div>
        </div>
        <button
          type="submit"
          onClick={handleAnalyze}
          disabled={!attachment || Boolean(processing)}
          className="flex items-center gap-2 rounded-lg border bg-blue-200 px-2 py-2 text-sm text-blue-900 hover:bg-blue-500 hover:text-white disabled:bg-gray-200 disabled:text-gray-400 md:px-4 md:py-2 md:text-lg"
        >
          {processing && (
            <>
              <BounceLoader size={20} />
              <span>Processing...</span>
            </>
          )}
          {!processing && 'Analyze'}
        </button>
      </form>
    </div>
  );
};

export default ImageAnalyzer;
